import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Typography,
  Popconfirm,
  Tag,
  Switch,
  Alert,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

const { Text } = Typography;
const { TextArea } = Input;

const ReportSectionManager = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // 板块列表
  const { data: sections = [], isLoading } = useQuery(
    'reportSections',
    async () => {
      const res = await axios.get('/api/report-sections');
      return res.data;
    }
  );

  // 可用数据源（近30天出现过的源）
  const { data: availableSources = [] } = useQuery(
    'reportSectionSources',
    async () => {
      const res = await axios.get('/api/report-sections/available-sources');
      return res.data;
    }
  );

  const invalidate = () => queryClient.invalidateQueries('reportSections');

  const createMutation = useMutation(
    (values) => axios.post('/api/report-sections', values),
    {
      onSuccess: () => {
        message.success('板块已创建');
        invalidate();
        setIsModalVisible(false);
      },
      onError: (e) => message.error(`创建失败: ${e.response?.data?.error || e.message}`)
    }
  );

  const updateMutation = useMutation(
    ({ id, values }) => axios.put(`/api/report-sections/${id}`, values),
    {
      onSuccess: () => {
        message.success('板块已更新');
        invalidate();
        setIsModalVisible(false);
        setEditRecord(null);
      },
      onError: (e) => message.error(`更新失败: ${e.response?.data?.error || e.message}`)
    }
  );

  const deleteMutation = useMutation(
    (id) => axios.delete(`/api/report-sections/${id}`),
    {
      onSuccess: () => {
        message.success('板块已删除');
        invalidate();
      },
      onError: (e) => message.error(`删除失败: ${e.response?.data?.error || e.message}`)
    }
  );

  const reorderMutation = useMutation(
    (ids) => axios.post('/api/report-sections/reorder', { ids }),
    {
      onSuccess: invalidate,
      onError: (e) => message.error(`排序失败: ${e.response?.data?.error || e.message}`)
    }
  );

  const resetMutation = useMutation(
    () => axios.post('/api/report-sections/reset-default'),
    {
      onSuccess: () => {
        message.success('已重置为默认板块配置');
        invalidate();
      },
      onError: (e) => message.error(`重置失败: ${e.response?.data?.error || e.message}`)
    }
  );

  const openCreate = () => {
    setEditRecord(null);
    form.resetFields();
    form.setFieldsValue({ max_items: 2, is_active: true, sources: [] });
    setIsModalVisible(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue({
      title: record.title,
      group_label: record.group_label || '',
      description: record.description,
      sources: record.sources || [],
      max_items: record.max_items,
      focus_companies: record.focus_companies || '',
      is_active: record.is_active === 1
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      group_label: values.group_label || null,
      focus_companies: values.focus_companies || null,
      sources: Array.isArray(values.sources) && values.sources.length > 0 ? values.sources : null
    };
    if (editRecord) {
      updateMutation.mutate({ id: editRecord.id, values: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  // 上移/下移：交换后提交完整顺序
  const moveSection = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    const ids = sections.map(s => s.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderMutation.mutate(ids);
  };

  const columns = [
    {
      title: '顺序',
      key: 'position',
      width: 100,
      render: (_, record, index) => (
        <Space size={4}>
          <Text>{index + 1}</Text>
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => moveSection(index, -1)}
          />
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === sections.length - 1}
            onClick={() => moveSection(index, 1)}
          />
        </Space>
      )
    },
    {
      title: '分组',
      dataIndex: 'group_label',
      key: 'group_label',
      width: 130,
      render: (v) => v ? <Tag color="purple">{v}</Tag> : <Text type="secondary">-</Text>
    },
    {
      title: '板块标题',
      dataIndex: 'title',
      key: 'title',
      width: 220,
      render: (v, record) => (
        <Tooltip title={record.description} placement="topLeft" overlayStyle={{ maxWidth: 480 }}>
          <Text strong>{v}</Text>
        </Tooltip>
      )
    },
    {
      title: '数据源',
      dataIndex: 'sources',
      key: 'sources',
      render: (v) => {
        if (!v || v.length === 0) return <Tag color="blue">全部源</Tag>;
        return (
          <Space size={[0, 4]} wrap>
            {v.map(s => <Tag key={s}>{s}</Tag>)}
          </Space>
        );
      }
    },
    {
      title: '条数上限',
      dataIndex: 'max_items',
      key: 'max_items',
      width: 90,
      align: 'center'
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (v, record) => (
        <Switch
          size="small"
          checked={v === 1}
          checkedChildren="启用"
          unCheckedChildren="停用"
          onChange={(checked) => updateMutation.mutate({ id: record.id, values: { is_active: checked } })}
        />
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="确定删除该板块？"
            onConfirm={() => deleteMutation.mutate(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Alert
        message="日报板块配置"
        description="这里配置正式日报的主题板块。每个板块可设置筛选要求（喂给 AI 的指令）、数据源范围、条数上限和重点关注公司。修改后下一次日报生成即生效。板块按顺序输出，宏观新闻始终在最前（不在此配置）。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Card
        title={
          <Space>
            <span>板块列表</span>
            <Tag color="blue">{sections.length} 个</Tag>
            <Tag color="green">{sections.filter(s => s.is_active === 1).length} 启用</Tag>
          </Space>
        }
        extra={
          <Space wrap>
            <Popconfirm
              title="将清空当前所有板块并恢复为默认的 14 个板块，确定？"
              onConfirm={() => resetMutation.mutate()}
            >
              <Button icon={<ReloadOutlined />} loading={resetMutation.isLoading}>
                重置为默认
              </Button>
            </Popconfirm>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              添加板块
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={sections}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title={editRecord ? '编辑板块' : '添加板块'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          setEditRecord(null);
        }}
        confirmLoading={createMutation.isLoading || updateMutation.isLoading}
        width={720}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="板块标题"
            rules={[{ required: true, message: '请输入板块标题' }]}
          >
            <Input placeholder="例如：AI 芯片" />
          </Form.Item>

          <Form.Item
            name="group_label"
            label="分组标题（可选）"
            help="仅需在每个分组的第一个板块上填写，如：一、AI 算力栈"
          >
            <Input placeholder="例如：一、AI 算力栈" />
          </Form.Item>

          <Form.Item
            name="description"
            label="筛选要求"
            rules={[{ required: true, message: '请输入筛选要求' }]}
            help="直接喂给 AI 的筛选指令，描述该板块要收什么新闻、排除什么、有什么特殊规则"
          >
            <TextArea rows={5} placeholder="例如：TPU / GPU / ASIC 产品更新，GPU vs XPU 竞争格局……" />
          </Form.Item>

          <Form.Item
            name="sources"
            label="数据源限制"
            help="留空 = 允许全部数据源"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="留空表示全部数据源"
              options={availableSources.map(s => ({
                value: s.source,
                label: `${s.source}（近30天 ${s.count} 条）`
              }))}
            />
          </Form.Item>

          <Form.Item
            name="max_items"
            label="条数上限"
            rules={[{ required: true, message: '请设置条数上限' }]}
          >
            <InputNumber min={1} max={10} />
          </Form.Item>

          <Form.Item
            name="focus_companies"
            label="重点关注公司（可选）"
            help="ticker 或公司名列表，命中相关新闻时提高排序优先级"
          >
            <TextArea rows={3} placeholder="例如：NVDA、AMD、AVGO、TSM（台积电）……" />
          </Form.Item>

          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReportSectionManager;
